import { useMemo, useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';
import { ProjectBriefsService } from '@inference.engine/workspace-api-client/web';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { workspaceUsersQuery } from '@/entities/project-member';
import {
  boardBriefsQueryOptions,
  BRIEF_TYPE_LABEL,
  BRIEFS_QUERY_KEYS,
  useUpdateAnyBriefCustomColumnValueMutation,
  type Brief,
  type ProjectBriefsGroup,
} from '@/entities/brief';
import { type Tracker, type TrackerLayout } from '@/entities/tracker';
import { workspaceHttp } from '@/lib/api/http-client';
import { useDebounce } from '@/shared/hooks';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import {
  usePersistentFilterRules,
  usePersistentFilters,
  useRecentFilterColumns,
} from './usePersistentFilters';
import { useHasEnabledAutomations } from './useHasEnabledAutomations';
import { scopeAssetsColumnsToProject } from './column-config';
import { useBoardLayout } from './useBoardLayout';
import {
  COLUMN_TYPE_LABEL_KEY,
  defaultStatusOptions,
  type CustomColumn,
  type CustomColumnType,
} from './board-columns';
import {
  buildFilterColumns,
  compileFilters,
  compileRules,
  countFilterValues,
  filterMatchColumns,
  passesFilters,
  passesSearch,
  type FilterColumnSpec,
  type FilterMatchColumn,
  type RawValueReader,
} from './filtering';
import { useFilterOptionLabels } from './useFilterOptionLabels';
import {
  buildGroups,
  countGroups,
  checkboxColumnSpec,
  customGroupSpec,
  peopleColumnSpec,
  singleValueSpec,
  statusColumnSpec,
  CUSTOM_GROUP_BY,
  GROUP_VALUE_KEY,
  NO_GROUP_ID,
  type GroupBucket,
  type GroupingSpec,
} from './grouping';
import { compareByColumn, compareBySortId, cycleColumnSort, type ColumnSort } from './sorting';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';

export {
  COLUMN_TYPE_LABEL_KEY,
  COMMENTS_COLUMN_WIDTH,
  CUSTOM_COLUMN_DEFAULT_WIDTH,
  CUSTOM_COLUMN_MIN_WIDTH,
  CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE,
  DEFAULT_COLUMN_ORDER,
  ITEM_COLUMN_DEFAULT_WIDTH,
  ITEM_COLUMN_MIN_WIDTH,
  REORDERABLE_COLUMNS,
  STATUS_COLOR_PALETTE,
  defaultStatusOptions,
  storedColumnType,
  type CustomColumn,
  type CustomColumnType,
  type StatusOption,
} from './board-columns';
export type { ScrollContainersRef, SyncedScrollLeftRef } from './useBoardLayout';

/* eslint-disable i18next/no-literal-string -- reserved brief field names, never rendered */
const PROJECT_FILTER_KEY = 'project';
const BRIEF_TYPE_FILTER_KEY = 'brief_type';
/* eslint-enable i18next/no-literal-string */

/** Categorical column types a briefs board can group by. */
const GROUPABLE_TYPES = new Set<CustomColumnType>([
  'status',
  'checkbox',
  'people',
  'brief_type',
  'project',
]);
/** Built-in grouping (no column needed): briefs bucketed by their project. */
// eslint-disable-next-line i18next/no-literal-string -- grouping field name persisted on the tracker
const PROJECT_GROUP_BY = 'project';

/**
 * Local mirror of the table's group/row shape (structurally a `BoardGroup` /
 * `BoardRowItem`), kept here so this lib module doesn't import from the ui
 * layer. Consumers pass these straight to `<TrackersBoardTable>`.
 */
interface BoardRowVM {
  id: string;
  title: string;
  customColumnValues?: Record<string, unknown> | undefined;
  customColumnMeta?: Brief['customColumnMeta'];
  /** workspace-api internal project id (grouping / project-title match). */
  projectId?: string;
  /** platform project id — for project-scoped cell actions (route/mutation). */
  platformProjectId?: string;
  projectTitle?: string | null;
  briefType?: Brief['briefType'] | null;
}
interface BoardGroupVM {
  id: string;
  title: string;
  color?: string;
  items: BoardRowVM[];
  itemCount: number;
  /** Rows in this group before search and filters narrowed them. */
  unfilteredItemCount: number;
}

export interface ProjectGroupViewModel {
  project: {
    /** workspace-api internal project id — used for board grouping/layout keys. */
    id: string;
    /**
     * Platform project id — the id `projects/:platformProjectId/*` routes are
     * scoped to. Use this (NOT `id`, NOT `brief.projectId`) for any project-
     * scoped API call or route. Null for drafts (no platform project).
     */
    platformProjectId: string | null;
    title: string | null;
  };
  briefs: Brief[];
  briefCount: number;
}

/**
 * Starter columns seeded for workspaces that have never saved a layout, so
 * first-time users land on a useful board instead of a bare Item column.
 * They are ordinary custom columns — fully editable, reorderable, and
 * deletable — and only persist once the user makes their first layout edit.
 * Ids are stable constants so per-brief values keyed on them survive the
 * transition from "seeded defaults" to "saved layout".
 */
export const defaultStarterColumns = (): CustomColumn[] => [
  {
    id: 'starter_brief_type',
    label: i18n.t('trackers:trackersBoard.useTrackersBoard.briefType'),
    type: 'brief_type',
  },
  {
    id: 'starter_status',
    label: i18n.t('trackers:trackersBoard.useTrackersBoard.status'),
    type: 'status',
    statusOptions: defaultStatusOptions(),
  },
  {
    id: 'starter_due_date',
    label: i18n.t('trackers:trackersBoard.useTrackersBoard.dueDate'),
    type: 'date',
  },
  {
    id: 'starter_owner',
    label: i18n.t('trackers:trackersBoard.useTrackersBoard.owner'),
    type: 'people',
  },
  {
    id: 'starter_assets',
    label: i18n.t('trackers:trackersBoard.useTrackersBoard.projectAssets'),
    type: 'assets',
  },
  {
    id: 'starter_notes',
    label: i18n.t('trackers:trackersBoard.useTrackersBoard.notes'),
    type: 'text',
  },
];

/** Synthetic group id used when the board isn't grouped (Group by: None). */
// eslint-disable-next-line i18next/no-literal-string -- synthetic group id
export const FLAT_GROUP_ID = '__all__';

/**
 * The header an older board saved for its assets column no longer describes
 * what the cell shows, so it is rewritten on the way in. Same key a new column
 * takes its label from, so the two cannot drift.
 */
const adoptBriefColumns = (columns: CustomColumn[]): CustomColumn[] =>
  scopeAssetsColumnsToProject(columns, i18n.t(`trackers:${COLUMN_TYPE_LABEL_KEY.assets}`));

/**
 * Board state for a briefs-source tracker. The tracker row provides the
 * persisted layout (seeded once) and receives debounced layout PATCHes;
 * `groupBy` ('project' | null) also lives on the tracker row so the whole
 * workspace shares the same grouping.
 */
export function useTrackersBoard(tracker: Tracker) {
  const { t } = useTranslation('trackers');
  const workspaceId = useCurrentWorkspaceId();
  const queryClient = useQueryClient();
  const hasEnabledAutomations = useHasEnabledAutomations(tracker.id);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  // Dynamic, opt-in filters. Keyed by column id — plus two reserved keys for
  // briefs' intrinsic fields: 'brief_type' and 'project'. Presence of a key =
  // the filter is shown; its value array = selected values (empty = shown but
  // not narrowing). Persisted PER-USER in localStorage (see
  // usePersistentFilters): survives reload, private to this browser, never
  // shared with the workspace like the tracker's layout is.
  const [filters, setFilters] = usePersistentFilters(tracker.id);
  const [filterRules, setFilterRules] = usePersistentFilterRules(tracker.id);
  const [recentFilterColumns, noteFilterColumnUsed] = useRecentFilterColumns(tracker.id);

  // Sort selection (single, session-local). `null` = Default order, which
  // respects user-defined drag order via `briefOrderOverrides`. Picking any
  // other option overrides drag order and sorts each group's briefs
  // accordingly. Library uses the same pattern.
  const [sortId, setSortIdState] = useState<string | null>(null);
  // Per-column sort (click a header). Takes precedence over `sortId`; the two
  // are mutually exclusive so only one sort is ever active.
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  const setSortId = useCallback((next: string | null) => {
    setSortIdState(next);
    if (next) setColumnSort(null);
  }, []);
  const toggleColumnSort = useCallback((columnId: string) => {
    setColumnSort((prev) => cycleColumnSort(prev, columnId));
    setSortIdState(null);
  }, []);

  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(new Set());
  const [hasInitializedExpanded, setHasInitializedExpanded] = useState(false);
  // Column/custom groupings start fully expanded; this session-local set tracks
  // the buckets the user has collapsed (project grouping uses the persisted
  // `expandedProjectIds` set above instead).
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(new Set());

  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [briefOrderOverrides, setBriefOrderOverrides] = useState<Map<string, string[]>>(new Map());
  const [isDraggingGroup, setIsDraggingGroup] = useState(false);
  const expandedBeforeDragRef = useRef<Set<string>>(new Set());

  // Briefs keep their own drag order and expansion in the same layout blob.
  const briefsLayoutExtras = useMemo(
    () => ({
      groupOrder,
      briefOrderOverrides: Object.fromEntries(briefOrderOverrides),
      expandedProjectIds: Array.from(expandedProjectIds),
    }),
    [groupOrder, briefOrderOverrides, expandedProjectIds],
  );
  const seedBriefsExtras = useCallback((data: TrackerLayout) => {
    if (data.groupOrder) setGroupOrder(data.groupOrder);
    if (data.briefOrderOverrides) {
      setBriefOrderOverrides(new Map(Object.entries(data.briefOrderOverrides)));
    }
    if (data.expandedProjectIds) {
      setExpandedProjectIds(new Set(data.expandedProjectIds));
      // Server already told us which groups should be expanded — don't run
      // the auto-expand-all heuristic later.
      setHasInitializedExpanded(true);
    }
  }, []);

  // Everything about how the board is laid out — columns, widths, custom
  // groups, dashboard, views — plus getting it saved. Shared with the other
  // boards; the briefs-only keys ride along through `extraPayload`.
  const layout = useBoardLayout({
    tracker,
    starterColumns: defaultStarterColumns,
    adoptColumns: adoptBriefColumns,
    extraPayload: briefsLayoutExtras,
    onSeed: seedBriefsExtras,
  });
  const {
    columnOrder,
    columnWidths,
    columnDefs,
    customColumns,
    customGroups,
    itemColumnWidth,
    setItemColumnWidth,
    setColumnWidth,
    reorderColumns,
    tableMinContentWidth,
    addCustomColumn,
    addCustomColumnAfter,
    deleteColumn,
    renameColumn,
    updateStatusOptions,
    updateButtonCaption,
    updateFormulaExpression,
    setCustomGroups,
    addGroup,
    renameGroup,
    deleteGroup,
    groupById,
    setGroupBy,
    dashboard,
    setDashboard,
    views,
    setViews,
    scrollContainersRef,
    syncedScrollLeftRef,
  } = layout;

  const briefsQuery = useQuery(
    boardBriefsQueryOptions({
      workspaceId: workspaceId ?? '',
      trackerId: tracker.id,
    }),
  );

  // Projects are the ones that have briefs: the rows carry their titles and
  // platform ids, so the board needs no workspace project list. Drafts have no
  // platform id and no project-scoped route, so they are left out.
  const briefProjects = useMemo(
    () =>
      (briefsQuery.data ?? []).flatMap((group) =>
        group.platformProjectId
          ? [{ id: group.projectId, platformProjectId: group.platformProjectId }]
          : [],
      ),
    [briefsQuery.data],
  );

  // brief.projectId is the internal id; project-scoped routes need the platform
  // id. This maps internal → platform for scoped calls that only have a brief.
  const platformProjectIdByInternalId = useMemo(
    () => new Map(briefProjects.map((p) => [p.id, p.platformProjectId])),
    [briefProjects],
  );

  const projectTitleByInternalId = useMemo(
    () =>
      new Map(
        (briefsQuery.data ?? []).map((group) => [
          group.projectId,
          group.projectTitle ?? t('trackersBoard.useTrackersBoard.untitledProject'),
        ]),
      ),
    [briefsQuery.data, t],
  );

  // Options for the toolbar's project filter: the projects that have briefs.
  const filterProjects = useMemo(
    () => [...projectTitleByInternalId].map(([id, title]) => ({ id, title })),
    [projectTitleByInternalId],
  );

  const filterOptionLabels = useFilterOptionLabels();

  const unfilteredBriefs = useMemo(
    () => (briefsQuery.data ?? []).flatMap((group) => group.briefs),
    [briefsQuery.data],
  );

  /** Project and Brief type are real brief fields, not custom-column values. */
  const readBriefValue: RawValueReader<Brief> = useCallback((brief, columnId) => {
    if (columnId === PROJECT_FILTER_KEY) return brief.projectId;
    if (columnId === BRIEF_TYPE_FILTER_KEY) return brief.briefType;
    return brief.customColumnValues?.[columnId];
  }, []);

  /** The two reserved brief fields, then the tracker's own filterable columns. */
  const filterColumns = useMemo<FilterColumnSpec[]>(
    () => [
      {
        id: BRIEF_TYPE_FILTER_KEY,
        label: t('trackersBoard.useTrackersBoard.briefType'),
        kind: 'plain',
        options: Object.entries(BRIEF_TYPE_LABEL)
          .map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        id: PROJECT_FILTER_KEY,
        label: t('trackersBoard.useTrackersBoard.project'),
        kind: 'plain',
        options: filterProjects.map((p) => ({ value: p.id, label: p.title })),
      },
      ...buildFilterColumns(customColumns, unfilteredBriefs, readBriefValue, filterOptionLabels),
    ],
    [t, filterProjects, customColumns, unfilteredBriefs, readBriefValue, filterOptionLabels],
  );

  /**
   * What selections are matched against. Wider than `filterColumns`, which
   * hides a people column until the directory can name it: a persisted people
   * selection has to keep narrowing the board while those names load.
   */
  const matchColumns = useMemo<FilterMatchColumn[]>(
    () => [
      { id: BRIEF_TYPE_FILTER_KEY, kind: 'plain' },
      { id: PROJECT_FILTER_KEY, kind: 'plain' },
      ...filterMatchColumns(customColumns),
    ],
    [customColumns],
  );

  /** The type behind a filter key, the reserved brief fields included. */
  const filterColumnType = useCallback(
    (columnId: string): CustomColumnType | undefined => {
      if (columnId === BRIEF_TYPE_FILTER_KEY) return 'brief_type';
      if (columnId === PROJECT_FILTER_KEY) return 'project';
      return customColumns.find((c) => c.id === columnId)?.type;
    },
    [customColumns],
  );

  const compiledFilters = useMemo(
    () => [...compileFilters(filters, matchColumns), ...compileRules(filterRules, matchColumns)],
    [filters, filterRules, matchColumns],
  );

  /**
   * One instant for the whole board: the chip counts and the row matcher both
   * bucket dates against it, and two `new Date()`s can straddle midnight and
   * disagree about what is overdue.
   */
  const now = useMemo(() => new Date(), []);

  // Notes is a text column, not a fixed brief field, so "search the notes"
  // means searching every text column.
  const searchColumnIds = useMemo(
    () => customColumns.filter((c) => c.type === 'text').map((c) => c.id),
    [customColumns],
  );

  const filterValueCounts = useMemo(
    () => countFilterValues(filterColumns, unfilteredBriefs, readBriefValue, now),
    [filterColumns, unfilteredBriefs, readBriefValue, now],
  );

  const groups = useMemo<ProjectGroupViewModel[]>(() => {
    const briefGroups: ProjectBriefsGroup[] = briefsQuery.data ?? [];
    const search = debouncedSearch.toLowerCase().trim();

    // A column-header sort, when active, overrides both the toolbar sort and
    // manual drag order.
    const sortColumn = columnSort
      ? customColumns.find((c) => c.id === columnSort.columnId)
      : undefined;
    const columnComparator =
      sortColumn && columnSort ? compareByColumn<Brief>(sortColumn, columnSort.dir) : null;
    const sortComparator = compareBySortId<Brief>(sortId, customColumns);

    // Project is applied to whole GROUPS as well as briefs: dropping the group
    // is what keeps a filtered-out project from rendering as an empty header.
    // Only the positive form can do that — `is not` leaves the group standing
    // and simply empties it, which the brief-level pass already handles.
    const projectFilter =
      compiledFilters.find((f) => f.columnId === PROJECT_FILTER_KEY && !f.negate)?.values ?? null;

    const result = briefGroups
      .filter((group) => !projectFilter || projectFilter.has(group.projectId))
      .map((group) => {
        let filteredBriefs = group.briefs.filter(
          (b) =>
            passesFilters(b, compiledFilters, readBriefValue, now) &&
            passesSearch(b, search, searchColumnIds, readBriefValue),
        );

        // Sort takes precedence over drag-order overrides. If no sort is
        // chosen (Default), apply the user's manual drag order.
        if (columnComparator) {
          filteredBriefs = [...filteredBriefs].sort(columnComparator);
        } else if (sortComparator) {
          filteredBriefs = [...filteredBriefs].sort(sortComparator);
        } else {
          const overrideOrder = briefOrderOverrides.get(group.projectId);
          if (overrideOrder) {
            const briefMap = new Map(filteredBriefs.map((b) => [b.id, b]));
            const ordered: Brief[] = [];
            for (const id of overrideOrder) {
              const brief = briefMap.get(id);
              if (brief) {
                ordered.push(brief);
                briefMap.delete(id);
              }
            }
            for (const brief of briefMap.values()) {
              ordered.push(brief);
            }
            filteredBriefs = ordered;
          }
        }

        return {
          project: {
            id: group.projectId,
            platformProjectId: group.platformProjectId || null,
            title: group.projectTitle,
          },
          briefs: filteredBriefs,
          briefCount: filteredBriefs.length,
        };
      })
      .filter((g) => g.briefCount > 0);

    if (groupOrder.length > 0) {
      const groupMap = new Map(result.map((g) => [g.project.id, g]));
      const ordered: ProjectGroupViewModel[] = [];
      for (const id of groupOrder) {
        const group = groupMap.get(id);
        if (group) {
          ordered.push(group);
          groupMap.delete(id);
        }
      }
      for (const group of groupMap.values()) {
        ordered.push(group);
      }
      result.length = 0;
      result.push(...ordered);
    }

    if (!hasInitializedExpanded && result.length > 0) {
      setExpandedProjectIds(new Set(result.map((g) => g.project.id)));
      setHasInitializedExpanded(true);
    }

    // Group by: None — flatten every group into one synthetic "All briefs"
    // section. Sort still applies (re-applied across the merged list so it
    // isn't merely per-group); drag order overrides don't (they're
    // per-project by nature).
    if (groupById !== 'project') {
      const allBriefs = result.flatMap((g) => g.briefs);
      if (columnComparator) {
        allBriefs.sort(columnComparator);
      } else if (sortComparator) {
        allBriefs.sort(sortComparator);
      }
      return [
        {
          // Synthetic "all briefs" group — not a real project, so no platform id.
          project: {
            id: FLAT_GROUP_ID,
            platformProjectId: null,
            title: t('trackersBoard.useTrackersBoard.allBriefs'),
          },
          briefs: allBriefs,
          briefCount: allBriefs.length,
        },
      ];
    }

    return result;
  }, [
    briefsQuery.data,
    debouncedSearch,
    hasInitializedExpanded,
    groupOrder,
    briefOrderOverrides,
    compiledFilters,
    now,
    searchColumnIds,
    readBriefValue,
    customColumns,
    sortId,
    columnSort,
    groupById,
    t,
  ]);

  const totalBriefs = useMemo(() => groups.reduce((sum, g) => sum + g.briefCount, 0), [groups]);

  /**
   * Count of briefs in the workspace BEFORE search/filter/sort is applied.
   * Drives the "true" empty state in Trackers.tsx: only when this is zero do we
   * hide the toolbar and show "No briefs yet". A zero `totalBriefs` with a
   * non-zero `unfilteredTotalBriefs` is a filter miss — toolbar stays visible
   * so users can revise filters.
   */
  const unfilteredTotalBriefs = useMemo(
    () => (briefsQuery.data ?? []).reduce((sum, group) => sum + group.briefs.length, 0),
    [briefsQuery.data],
  );

  // Read off the compiled list, so a selection the board is not actually
  // applying (a since-deleted column, say) cannot suppress the "no briefs yet"
  // empty state.
  const hasActiveFilters = searchInput.length > 0 || compiledFilters.length > 0;

  const toggleProjectExpanded = useCallback(
    (groupId: string) => {
      // Column/custom groupings track COLLAPSED ids (default-expanded); project
      // (and flat) grouping uses the persisted EXPANDED set.
      if (groupById != null && groupById !== 'project') {
        setCollapsedGroupIds((prev) => {
          const next = new Set(prev);
          if (next.has(groupId)) next.delete(groupId);
          else next.add(groupId);
          return next;
        });
        return;
      }
      setExpandedProjectIds((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) next.delete(groupId);
        else next.add(groupId);
        return next;
      });
    },
    [groupById],
  );

  const expandAll = useCallback(() => {
    setExpandedProjectIds(new Set(groups.map((g) => g.project.id)));
  }, [groups]);

  const collapseAll = useCallback(() => {
    setExpandedProjectIds(new Set());
  }, []);

  const groupIds = useMemo(() => groups.map((g) => g.project.id), [groups]);

  const handleGroupDragStart = useCallback(() => {
    expandedBeforeDragRef.current = new Set(expandedProjectIds);
    setExpandedProjectIds(new Set());
    setIsDraggingGroup(true);
  }, [expandedProjectIds]);

  const handleGroupDragEnd = useCallback(
    (activeId: string, overId: string) => {
      setIsDraggingGroup(false);
      setExpandedProjectIds(expandedBeforeDragRef.current);

      if (activeId === overId) return;

      const currentIds = groupIds;
      const oldIndex = currentIds.indexOf(activeId);
      const newIndex = currentIds.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return;

      setGroupOrder(arrayMove(currentIds, oldIndex, newIndex));
    },
    [groupIds],
  );

  /**
   * Clone a custom column, then carry every brief's value for the source
   * column across to the copy. The column itself is the shared layout hook's
   * job; what belongs here is the cell data, which only a briefs board has.
   *
   * The copy is applied optimistically to the cache and persisted with one
   * PATCH per brief in the background — no waiting required for the UI to
   * reflect it.
   */
  const duplicateColumn = useCallback(
    (columnId: string) => {
      const newId = layout.duplicateColumn(columnId);
      if (newId === null || !workspaceId) return;
      const briefsQueryKey = BRIEFS_QUERY_KEYS.allByWorkspace(workspaceId);

      // Optimistic cache update: every brief that had a value for the source
      // column instantly shows the same value under the new column id.
      queryClient.setQueryData<ProjectBriefsGroup[] | undefined>(briefsQueryKey, (groupsData) => {
        if (!groupsData) return groupsData;
        return groupsData.map((g) => ({
          ...g,
          briefs: g.briefs.map((b) => {
            const values = b.customColumnValues as Record<string, unknown> | undefined;
            const sourceValue = values?.[columnId];
            if (sourceValue === undefined || sourceValue === null) return b;
            return {
              ...b,
              customColumnValues: { ...(values ?? {}), [newId]: sourceValue },
            };
          }),
        }));
      });

      // Persist: one PATCH per brief that had a non-null source value. The
      // backend merge semantics let us send only `{ [newId]: value }` without
      // touching other columns. Fire-and-forget — on failure the next refetch
      // will reconcile (and the column itself is what users mostly care
      // about; the missing values would just appear blank on those briefs).
      for (const group of groups) {
        // Route is scoped by platform id; brief.projectId is the internal id.
        const platformProjectId = group.project.platformProjectId;
        if (!platformProjectId) continue;
        for (const brief of group.briefs) {
          const values = brief.customColumnValues as Record<string, unknown> | undefined;
          const sourceValue = values?.[columnId];
          if (sourceValue === undefined || sourceValue === null) continue;
          void ProjectBriefsService.update({
            client: workspaceHttp,
            path: { platformProjectId, briefId: brief.id },
            body: { customColumnValues: { [newId]: sourceValue } },
          }).catch((err: unknown) => {
            // eslint-disable-next-line no-console
            console.error(
              `[TrackersBoard] Failed to copy duplicated column value for brief ${brief.id}:`,
              err,
            );
          });
        }
      }
    },
    [layout, groups, queryClient, workspaceId],
  );

  const reorderBriefs = useCallback(
    (projectId: string, activeId: string, overId: string) => {
      if (activeId === overId) return;

      const group = groups.find((g) => g.project.id === projectId);
      if (!group) return;

      const briefIds = group.briefs.map((b) => b.id);
      const oldIndex = briefIds.indexOf(activeId);
      const newIndex = briefIds.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return;

      setBriefOrderOverrides((prev) => {
        const next = new Map(prev);
        next.set(projectId, arrayMove(briefIds, oldIndex, newIndex));
        return next;
      });
    },
    [groups],
  );

  // ── Grouping. Briefs bucket by their project (the built-in default) or by
  // any categorical column, all through the shared engine. `groupById` is a
  // column id, the built-in 'project' sentinel, or null (treated as project).
  const groupColumn = useMemo(
    () => customColumns.find((c) => c.id === groupById && GROUPABLE_TYPES.has(c.type)),
    [customColumns, groupById],
  );

  // Roster — only fetched when grouping by a People/Owner column (Platform
  // 401 locally → empty → every brief lands in "No owner").
  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId) && groupColumn?.type === 'people',
  });
  const peopleBuckets = useMemo<GroupBucket[]>(
    () => (usersQuery.data?.data ?? []).map((u) => ({ id: u.id, label: u.name })),
    [usersQuery.data],
  );
  const projectBuckets = useMemo<GroupBucket[]>(
    () => filterProjects.map((p) => ({ id: p.id, label: p.title })),
    [filterProjects],
  );

  const isCustomGrouping = groupById === CUSTOM_GROUP_BY;

  const groupingSpec = useMemo<GroupingSpec<Brief> | null>(() => {
    if (groupColumn) {
      switch (groupColumn.type) {
        case 'status':
          return statusColumnSpec(groupColumn);
        case 'checkbox':
          return checkboxColumnSpec(groupColumn);
        case 'people':
          return peopleColumnSpec(groupColumn, peopleBuckets);
        case 'project':
          return singleValueSpec<Brief>({
            buckets: projectBuckets,
            getId: (b) => b.projectId,
            noneLabel: t('trackersBoard.useTrackersBoard.noProject'),
          });
        case 'brief_type':
          return singleValueSpec<Brief>({
            buckets: Object.entries(BRIEF_TYPE_LABEL).map(([id, label]) => ({ id, label })),
            getId: (b) => b.briefType ?? undefined,
            noneLabel: t('trackersBoard.useTrackersBoard.noType'),
          });
        default:
          return null;
      }
    }
    if (isCustomGrouping) return customGroupSpec(customGroups);
    return null; // project sentinel / flat → project-aware groups
  }, [groupColumn, isCustomGrouping, customGroups, peopleBuckets, projectBuckets, t]);

  // ── Custom-group management (Group by: Custom). Edits go through the same
  // debounced layout PATCH as columns; a brief joins a group by writing the
  // reserved `__group__` key on its own custom values (merged, not replaced).
  const updateBriefValue = useUpdateAnyBriefCustomColumnValueMutation({
    workspaceId: workspaceId ?? '',
    refetchForAutomations: hasEnabledAutomations,
  });
  const briefById = useMemo(() => {
    const map = new Map<string, Brief>();
    for (const g of groups) for (const b of g.briefs) map.set(b.id, b);
    return map;
  }, [groups]);
  const moveBriefToGroup = useCallback(
    (briefId: string, toGroupId: string) => {
      const brief = briefById.get(briefId);
      if (!brief) return;
      const platformProjectId = platformProjectIdByInternalId.get(brief.projectId);
      if (!platformProjectId) return;
      updateBriefValue.mutate({
        projectId: platformProjectId,
        briefId,
        columnId: GROUP_VALUE_KEY,
        value: toGroupId === NO_GROUP_ID ? null : toGroupId,
      });
    },
    [briefById, platformProjectIdByInternalId, updateBriefValue],
  );

  const tableGroups = useMemo<BoardGroupVM[]>(() => {
    const toRow = (b: Brief): BoardRowVM => ({
      id: b.id,
      title: b.title,
      customColumnValues: b.customColumnValues as Record<string, unknown> | undefined,
      customColumnMeta: b.customColumnMeta ?? null,
      projectId: b.projectId,
      platformProjectId: platformProjectIdByInternalId.get(b.projectId),
      projectTitle: projectTitleByInternalId.get(b.projectId),
      briefType: b.briefType ?? null,
    });
    const unfilteredGroups = briefsQuery.data ?? [];
    if (!groupingSpec) {
      // Project (or flat) — reuse the project-aware groups so per-project
      // ordering/expansion is preserved.
      const unfilteredByProject = new Map(
        unfilteredGroups.map((g) => [g.projectId, g.briefs.length]),
      );
      return groups.map((g) => ({
        id: g.project.id,
        title: g.project.title ?? t('trackersBoard.useTrackersBoard.untitledProject'),
        items: g.briefs.map(toRow),
        itemCount: g.briefCount,
        // The flat group holds every project's briefs, so no single project's
        // count describes it.
        unfilteredItemCount:
          g.project.id === FLAT_GROUP_ID
            ? unfilteredTotalBriefs
            : (unfilteredByProject.get(g.project.id) ?? 0),
      }));
    }
    // Every declared bucket renders on this path, narrowed to nothing or not,
    // so each one needs its own before-filtering count to pick its empty copy.
    const unfilteredCounts = countGroups(
      unfilteredGroups.flatMap((g) => g.briefs),
      groupingSpec,
    );
    return buildGroups(
      groups.flatMap((g) => g.briefs),
      groupingSpec,
      toRow,
    ).map((g) => ({ ...g, unfilteredItemCount: unfilteredCounts.get(g.id) ?? 0 }));
  }, [
    groups,
    groupingSpec,
    platformProjectIdByInternalId,
    projectTitleByInternalId,
    t,
    briefsQuery.data,
    unfilteredTotalBriefs,
  ]);

  const tableGroupIds = useMemo(() => tableGroups.map((g) => g.id), [tableGroups]);

  const groupByOptions = useMemo(() => {
    const cols = customColumns.filter((c) => GROUPABLE_TYPES.has(c.type));
    const hasProjectColumn = cols.some((c) => c.type === 'project');
    const opts: Array<{ value: string; label: string }> = [];
    // Built-in project grouping — unless a Project column already covers it.
    if (!hasProjectColumn)
      opts.push({ value: PROJECT_GROUP_BY, label: t('trackersBoard.useTrackersBoard.project') });
    for (const c of cols) opts.push({ value: c.id, label: c.label });
    // User-defined Custom groups, always available.
    opts.push({ value: CUSTOM_GROUP_BY, label: t('trackersBoard.useTrackersBoard.custom') });
    return opts;
  }, [customColumns, t]);

  return {
    groups,
    groupIds,
    // internal project id → platform project id, for consumers that only hold a
    // brief (whose `projectId` is the internal id) but must call a scoped route.
    platformProjectIdByInternalId,
    // Generic grouping outputs (project or any categorical column).
    tableGroups,
    tableGroupIds,
    groupByOptions,
    totalBriefs,
    groupById,
    setGroupBy,
    // Custom groups (Group by: Custom).
    isCustomGrouping,
    customGroupByValue: CUSTOM_GROUP_BY,
    customGroups,
    setCustomGroups,
    addGroup,
    renameGroup,
    deleteGroup,
    moveBriefToGroup,
    isLoading: briefsQuery.isLoading,
    isError: briefsQuery.isError,
    searchInput,
    setSearchInput,
    filters,
    setFilters,
    filterRules,
    setFilterRules,
    filterProjects,
    filterColumns,
    filterColumnType,
    activeFilterCount: compiledFilters.length,
    filterValueCounts,
    recentFilterColumns,
    noteFilterColumnUsed,
    sortId,
    setSortId,
    unfilteredTotalBriefs,
    hasActiveFilters,
    columnSort,
    toggleColumnSort,
    // Column/custom groupings expand every bucket by default, minus the ones
    // the user collapsed this session; the synthetic flat group (no project
    // grouping) is always expanded — it IS the whole board.
    expandedProjectIds: groupingSpec
      ? new Set(tableGroupIds.filter((id) => !collapsedGroupIds.has(id)))
      : groupById !== 'project'
        ? new Set([...expandedProjectIds, FLAT_GROUP_ID])
        : expandedProjectIds,
    toggleProjectExpanded,
    expandAll,
    collapseAll,
    isDraggingGroup,
    handleGroupDragStart,
    handleGroupDragEnd,
    reorderBriefs,
    columnOrder,
    columnWidths,
    reorderColumns,
    setColumnWidth,
    itemColumnWidth,
    setItemColumnWidth,
    scrollContainersRef,
    syncedScrollLeftRef,
    tableMinContentWidth,
    customColumns,
    columnDefs,
    addCustomColumn,
    addCustomColumnAfter,
    duplicateColumn,
    deleteColumn,
    updateStatusOptions,
    updateButtonCaption,
    updateFormulaExpression,
    renameColumn,
    // Dashboard view: state + setter — persistence rides the same
    // debounced layout PATCH as columns/widths.
    dashboard,
    setDashboard,
    // Enabled views (ordered) + setter — same debounced layout PATCH.
    views,
    setViews,
  };
}
